import * as React from 'react';
import Button from '@mui/material/Button';
import {
    Alert,
    Box,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Pagination,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { paginateChallenges } from '../Api';
import { useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid/DataGrid/DataGrid';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { GridCellParams } from '@mui/x-data-grid/models/params/gridCellParams';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Dayjs } from 'dayjs';
import {
    ChallengeAdminListItemDto,
    ChallengeAdminSort,
    CHALLENGE_STATE_OPTIONS,
    challengeStateLabel,
} from '../models/dtos/ChallengeAdminDto';

type BattlesListState = {
    after: Date | null;
    before: Date | null;
    afterDisplay: Dayjs | null;
    beforeDisplay: Dayjs | null;
    loaded: boolean;
    currentPage: number;
    limit: number;
    totalPages: number;
    totalItems: number;
    stateFilter: number | null;
    userId: string;
    matchId: string;
    isPrivate: boolean | null;
    searchTerm: string;
    sort: ChallengeAdminSort;
    battles: ChallengeAdminListItemDto[];
    loadError: string | null;
};

type BattlesListPanelProps = {
    /** When set, list is locked to this user and the User ID filter is hidden. */
    lockedUserId?: number;
    showTitle?: boolean;
    compact?: boolean;
};

export default function BattlesListPanel({
    lockedUserId,
    showTitle = true,
    compact = false,
}: BattlesListPanelProps) {
    const defaultLimit = 25;
    const lockedUserIdStr = lockedUserId != null ? String(lockedUserId) : '';

    const [state, setState] = React.useState<BattlesListState>({
        after: null,
        before: null,
        afterDisplay: null,
        beforeDisplay: null,
        loaded: false,
        currentPage: 0,
        limit: defaultLimit,
        totalPages: 0,
        totalItems: 0,
        stateFilter: null,
        userId: lockedUserIdStr,
        matchId: '',
        isPrivate: null,
        searchTerm: '',
        sort: 'CREATED_AT_DESC',
        battles: [],
        loadError: null,
    });

    const stateRef = React.useRef(state);
    stateRef.current = state;
    const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const navigate = useNavigate();

    React.useEffect(() => {
        if (lockedUserId != null) {
            setState(prev => ({ ...prev, userId: String(lockedUserId) }));
        }
    }, [lockedUserId]);

    const stopRowClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const columns: GridColDef[] = React.useMemo(
        () => [
            {
                field: 'id',
                headerName: 'ID',
                width: 90,
                renderCell: (params: GridCellParams) => (
                    <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={e => {
                            stopRowClick(e);
                            navigate('/battles/' + params.row.id);
                        }}
                    >
                        {params.row.id}
                    </Link>
                ),
            },
            {
                field: 'createdAt',
                headerName: 'Created',
                width: 170,
                renderCell: (params: GridCellParams) => (
                    <>{params.row.createdAt ? new Date(params.row.createdAt).toLocaleString() : '—'}</>
                ),
            },
            {
                field: 'state',
                headerName: 'State',
                width: 110,
                renderCell: (params: GridCellParams) => <>{challengeStateLabel(params.row.state)}</>,
            },
            {
                field: 'match',
                headerName: 'Match',
                width: 280,
                renderCell: (params: GridCellParams) => {
                    const home = params.row.homeTeamName ?? '?';
                    const away = params.row.awayTeamName ?? '?';
                    const matchId = params.row.matchId as number | null;
                    const label = `${matchId != null ? `#${matchId} ` : ''}${home} vs ${away}`;
                    if (matchId == null) return <>{label}</>;
                    return (
                        <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={e => {
                                stopRowClick(e);
                                navigate('/matches/' + matchId);
                            }}
                            title="Open match (no separate team page in dashboard)"
                        >
                            {label}
                        </Link>
                    );
                },
            },
            {
                field: 'participants',
                headerName: 'Participants',
                width: 220,
                renderCell: (params: GridCellParams) => (
                    <>{params.row.participantNames ?? `— (${params.row.participantCount})`}</>
                ),
            },
            {
                field: 'creatorName',
                headerName: 'Creator',
                width: 160,
                renderCell: (params: GridCellParams) => {
                    const name = params.row.creatorName ?? '—';
                    const creatorUserId = params.row.creatorUserId as number | null;
                    if (creatorUserId == null) {
                        return <>{name}</>;
                    }
                    return (
                        <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={e => {
                                stopRowClick(e);
                                navigate('/users/' + creatorUserId);
                            }}
                        >
                            {name} (#{creatorUserId})
                        </Link>
                    );
                },
            },
            {
                field: 'moneyAccept',
                headerName: 'Stake',
                width: 90,
                renderCell: (params: GridCellParams) => <>{params.row.moneyAccept}</>,
            },
            {
                field: 'moneyToWin',
                headerName: 'Prize',
                width: 90,
                renderCell: (params: GridCellParams) => <>{params.row.moneyToWin}</>,
            },
            {
                field: 'isPrivate',
                headerName: 'Private',
                width: 90,
                renderCell: (params: GridCellParams) => <>{params.row.isPrivate ? 'Yes' : 'No'}</>,
            },
            {
                field: 'actions',
                headerName: 'Actions',
                width: 140,
                renderCell: (params: GridCellParams) => (
                    <Button
                        variant="contained"
                        fullWidth
                        style={{ height: '32px' }}
                        onClick={e => {
                            stopRowClick(e);
                            navigate('/battles/' + params.row.id);
                        }}
                    >
                        Details
                    </Button>
                ),
            },
        ],
        [navigate],
    );

    const loadPage = React.useCallback(
        async (page: number, patch?: Partial<BattlesListState>) => {
            const s = { ...stateRef.current, ...patch };
            const effectiveUserId =
                lockedUserId != null ? String(lockedUserId) : s.userId;
            setState(prev => ({
                ...prev,
                ...patch,
                userId: effectiveUserId,
                loaded: false,
                loadError: null,
            }));
            try {
                const userIdNum = effectiveUserId.trim() ? Number(effectiveUserId.trim()) : null;
                const matchIdNum = s.matchId.trim() ? Number(s.matchId.trim()) : null;
                const response = await paginateChallenges(page * s.limit, s.limit, {
                    state: s.stateFilter,
                    userId: userIdNum != null && !Number.isNaN(userIdNum) ? userIdNum : null,
                    matchId: matchIdNum != null && !Number.isNaN(matchIdNum) ? matchIdNum : null,
                    isPrivate: s.isPrivate,
                    after: s.after ? s.after.toISOString() : null,
                    before: s.before ? s.before.toISOString() : null,
                    searchTerm: s.searchTerm.trim() || null,
                    sort: s.sort,
                });
                const battles = response?.items ?? [];
                const totalItems = response?.totalItems ?? 0;
                const totalPages = Math.max(1, Math.ceil(totalItems / s.limit));
                setState(prev => ({
                    ...prev,
                    ...patch,
                    userId: effectiveUserId,
                    battles,
                    loaded: true,
                    totalPages,
                    totalItems,
                    currentPage: page,
                    loadError: null,
                }));
            } catch (e) {
                const msg = e instanceof Error ? e.message : 'Failed to load battles';
                setState(prev => ({
                    ...prev,
                    ...patch,
                    userId: effectiveUserId,
                    battles: [],
                    loaded: true,
                    totalPages: 0,
                    totalItems: 0,
                    loadError: msg,
                }));
            }
        },
        [lockedUserId],
    );

    React.useEffect(() => {
        loadPage(0);
    }, [loadPage]);

    React.useEffect(() => {
        return () => {
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        };
    }, []);

    const onSearchChange = (value: string) => {
        setState(prev => ({ ...prev, searchTerm: value }));
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            loadPage(0, { searchTerm: value, currentPage: 0 });
        }, 450);
    };

    const clearDates = async () => {
        await loadPage(0, {
            after: null,
            before: null,
            afterDisplay: null,
            beforeDisplay: null,
            currentPage: 0,
        });
    };

    const applyDates = async () => {
        await loadPage(0, {
            after: state.afterDisplay ? state.afterDisplay.toDate() : null,
            before: state.beforeDisplay ? state.beforeDisplay.toDate() : null,
            currentPage: 0,
        });
    };

    const resetFilters = async () => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
        await loadPage(0, {
            currentPage: 0,
            limit: 25,
            searchTerm: '',
            after: null,
            before: null,
            afterDisplay: null,
            beforeDisplay: null,
            stateFilter: null,
            userId: lockedUserIdStr,
            matchId: '',
            isPrivate: null,
            sort: 'CREATED_AT_DESC',
        });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack
                gap={compact ? '16px' : '24px'}
                sx={compact ? { width: '100%' } : { mx: 5, my: 5 }}
                justifyContent="center"
                alignItems="center"
            >
                {showTitle ? <Typography variant="h3">Battles</Typography> : null}
                {lockedUserId != null ? (
                    <Typography variant="body2" color="text.secondary" alignSelf="flex-start">
                        Showing battles for user #{lockedUserId}
                    </Typography>
                ) : null}

                <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 4, lg: 6 }} alignItems="center">
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <DateTimePicker
                            label="After (optional)"
                            value={state.afterDisplay}
                            onChange={v => setState(prev => ({ ...prev, afterDisplay: v }))}
                            slotProps={{
                                textField: { fullWidth: true, size: 'small' },
                                field: { clearable: true },
                            }}
                        />
                    </Grid>
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <DateTimePicker
                            label="Before (optional)"
                            value={state.beforeDisplay}
                            onChange={v => setState(prev => ({ ...prev, beforeDisplay: v }))}
                            slotProps={{
                                textField: { fullWidth: true, size: 'small' },
                                field: { clearable: true },
                            }}
                        />
                    </Grid>
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <Stack direction="row" gap={1}>
                            <Button variant="outlined" fullWidth onClick={applyDates}>
                                Apply dates
                            </Button>
                            <Button variant="text" onClick={clearDates}>
                                Clear
                            </Button>
                        </Stack>
                    </Grid>
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <FormControl fullWidth size="small">
                            <InputLabel>State</InputLabel>
                            <Select
                                label="State"
                                value={state.stateFilter ?? ''}
                                onChange={e => {
                                    const v = e.target.value === '' ? null : Number(e.target.value);
                                    loadPage(0, { stateFilter: v, currentPage: 0 });
                                }}
                            >
                                <MenuItem value="">All</MenuItem>
                                {CHALLENGE_STATE_OPTIONS.map(o => (
                                    <MenuItem key={o.value} value={o.value}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Sort</InputLabel>
                            <Select
                                label="Sort"
                                value={state.sort}
                                onChange={e => {
                                    const sort = e.target.value as ChallengeAdminSort;
                                    loadPage(0, { sort, currentPage: 0 });
                                }}
                            >
                                <MenuItem value="CREATED_AT_DESC">Created ↓</MenuItem>
                                <MenuItem value="CREATED_AT_ASC">Created ↑</MenuItem>
                                <MenuItem value="MONEY_TO_WIN_DESC">Prize ↓</MenuItem>
                                <MenuItem value="MONEY_TO_WIN_ASC">Prize ↑</MenuItem>
                                <MenuItem value="STATE_ASC">State ↑</MenuItem>
                                <MenuItem value="STATE_DESC">State ↓</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Visibility</InputLabel>
                            <Select
                                label="Visibility"
                                value={state.isPrivate === null ? '' : state.isPrivate ? '1' : '0'}
                                onChange={e => {
                                    const raw = e.target.value;
                                    const isPrivate = raw === '' ? null : raw === '1';
                                    loadPage(0, { isPrivate, currentPage: 0 });
                                }}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="0">Public</MenuItem>
                                <MenuItem value="1">Private</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    {lockedUserId == null ? (
                        <Grid item xs={1} sm={1} md={1} lg={1}>
                            <TextField
                                label="User ID"
                                size="small"
                                fullWidth
                                value={state.userId}
                                onChange={e => setState(prev => ({ ...prev, userId: e.target.value }))}
                                onBlur={() => loadPage(0, { userId: state.userId, currentPage: 0 })}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        loadPage(0, { userId: state.userId, currentPage: 0 });
                                    }
                                }}
                            />
                        </Grid>
                    ) : null}
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <TextField
                            label="Match ID"
                            size="small"
                            fullWidth
                            value={state.matchId}
                            onChange={e => setState(prev => ({ ...prev, matchId: e.target.value }))}
                            onBlur={() => loadPage(0, { matchId: state.matchId, currentPage: 0 })}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    loadPage(0, { matchId: state.matchId, currentPage: 0 });
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={1} sm={2} md={2} lg={2}>
                        <TextField
                            label="Search (id, user, team)"
                            size="small"
                            fullWidth
                            value={state.searchTerm}
                            onChange={e => onSearchChange(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={1} sm={1} md={1} lg={1}>
                        <Button variant="outlined" fullWidth onClick={resetFilters}>
                            Reset
                        </Button>
                    </Grid>
                </Grid>

                {state.loaded && !state.loadError ? (
                    <Typography variant="body2" color="text.secondary" alignSelf="flex-start">
                        {state.totalItems} battle{state.totalItems === 1 ? '' : 's'} found
                        {state.after || state.before
                            ? ' (date filter active — Clear dates to see all)'
                            : ''}
                    </Typography>
                ) : null}

                <Pagination
                    count={state.totalPages}
                    size="medium"
                    page={state.currentPage + 1}
                    onChange={(_, p) => loadPage(p - 1, { currentPage: p - 1 })}
                />

                {state.loadError ? (
                    <Alert severity="error" sx={{ width: '100%' }}>
                        {state.loadError}
                        {state.loadError.includes('404') || state.loadError.includes('502') ? (
                            <>
                                {' '}
                                — Restart <strong>betup-server-analytics</strong> so the new{' '}
                                <code>/api/dashboard/challenges</code> endpoints are loaded.
                            </>
                        ) : null}
                    </Alert>
                ) : null}

                {!state.loaded ? (
                    <CircularProgress size="100px" style={{ marginBottom: '32px' }} />
                ) : state.battles.length === 0 && !state.loadError ? (
                    <Alert severity="info" sx={{ width: '100%' }}>
                        No battles found
                        {lockedUserId != null ? ` for user #${lockedUserId}` : ''}.
                    </Alert>
                ) : (
                    <DataGrid
                        sx={{ width: '100%', minHeight: 400 }}
                        rows={state.battles}
                        columns={columns}
                        getRowId={row => row.id}
                        initialState={{ pagination: { paginationModel: { pageSize: state.limit } } }}
                        pageSizeOptions={[state.limit]}
                        disableRowSelectionOnClick
                        autoHeight
                    />
                )}
                <Box />
            </Stack>
        </LocalizationProvider>
    );
}
